/**
 * ConfigurableBaseExplorer — configuration-driven entity explorer with windowed chrome.
 *
 * Orchestrates data, search, filters, and window state. Delegates:
 *   - table + pagination      → ResultsSection (top-level, memoized, generic)
 *   - fixed window shell      → WindowChrome (top-level, stateless)
 *   - drag behavior           → useWindowDrag
 *   - resize behavior         → useWindowResize
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './ConfigurableBaseExplorer.scss';
import {
  BaseEntity,
  BaseExplorerConfig,
  ColumnConfig,
  FilterParam,
  PresentationConfig,
} from '../../../types/baseExplorerConfig';
import { BaseExplorerConfigFactory } from '../../../utils/baseExplorerConfigFactory';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faMagnifyingGlass, faRotateRight, faSpinner, faTableColumns } from '@fortawesome/free-solid-svg-icons';

import { useFusionAuth } from '@fusionauth/react-sdk';
import { windowManager, generateWindowId } from '../../../utils/windowManager';
import {
  loadExplorerState,
  saveExplorerState,
  clearExplorerState,
  clampToViewport,
} from './explorerStatePersistence';
import { ExportConfirmDialog } from './ExportConfirmDialog';
import ExportFormatModal, { type ExplorerExportFormat } from './ExportFormatModal';
import { useExportConfirm } from './useExportConfirm';
import { downloadExportCsv } from './downloadExportCsv';
import { useMessages } from '../../common/AppMessages';
import { FilterPanel } from './FilterPanel';
import { FilterBadge } from './FilterBadge';
import WindowChrome, { HEADER_HEIGHT } from './WindowChrome';
import ResultsSection from './ResultsSection';
import { useWindowDrag } from './useWindowDrag';
import { useWindowResize } from './useWindowResize';
import { clampWindowToViewport, MAP_TOP_OFFSET } from './clampWindowToViewport';
import type { SortConfig, WindowPosition, WindowSize, WindowState } from './types';

export interface ConfigurableBaseExplorerProps<T extends BaseEntity> {
  config: BaseExplorerConfig<T>;
  isAuthenticated?: boolean;
  onEntitySelect?: (entity: T) => void;
  /** Optional dedicated View-button handler. See HOTDataGrid for semantics. */
  onEntityView?: (entity: T) => void;
  onEntityEdit?: (entity: T) => void;
  onEntityDelete?: (entity: T) => void;
  onEntityCreate?: () => void;
  className?: string;
  testId?: string;
  customToolbar?: React.ReactNode;
  /** Optional help element — always rendered last in the header so help
      appears on the far right across every explorer. Pass <ContextHelp .../>. */
  help?: React.ReactNode;
  initialSearchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  initialCrosslinkSearch?: { term: string; type: string };
  onCrosslinkSearchChange?: (search: { term: string; type: string } | null) => void;
  isWindowed?: boolean;
  windowTitle?: string;
  initialPosition?: WindowPosition;
  initialSize?: WindowSize;
  onWindowClose?: () => void;
  onWindowMinimize?: () => void;
  resizable?: boolean;
  draggable?: boolean;
  showCloseButton?: boolean;
  showMinimizeButton?: boolean;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  restoreRef?: React.MutableRefObject<(() => void) | null>;
  searchRef?: React.MutableRefObject<((term: string) => void) | null>;
  onZIndexUpdate?: (zIndex: number) => void;
  onActiveFiltersChange?: (filters: Record<string, any>) => void;
  initialFilters?: Record<string, any>;
  children?: React.ReactNode;
  refreshTrigger?: number;
  hiddenColumns?: string[];
  shownColumns?: string[];
}

interface ExplorerState<T> {
  entities: T[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  searchTerm: string;
  crosslinkSearch: { term: string; type: string } | null;
  sortConfig: SortConfig | null;
  filters: Record<string, any>;
}


export default function ConfigurableBaseExplorer<T extends BaseEntity>(props: ConfigurableBaseExplorerProps<T>) {
  const isMobileDevice = typeof window !== 'undefined' && window.innerWidth <= 768;
  const defaultPosition: WindowPosition = isMobileDevice ? { x: 5, y: 5 } : { x: 100, y: 100 };
  const defaultSize: WindowSize = isMobileDevice
    ? { width: window.innerWidth - 10, height: window.innerHeight - 10 }
    : { width: 800, height: 600 };

  const {
    config,
    isAuthenticated,
    onEntitySelect,
    onEntityView,
    onEntityEdit,
    onEntityDelete,
    className = '',
    testId = 'configurable-base-explorer',
    initialSearchTerm = '',
    onSearchChange,
    initialCrosslinkSearch,
    onCrosslinkSearchChange,
    isWindowed = false,
    windowTitle,
    initialPosition = defaultPosition,
    initialSize = defaultSize,
    onWindowClose,
    onWindowMinimize,
    resizable = true,
    draggable = true,
    showCloseButton = true,
    showMinimizeButton = true,
    minimizeRef,
    restoreRef,
    searchRef,
    onZIndexUpdate,
    onActiveFiltersChange,
    initialFilters,
    children,
    customToolbar,
    help,
    refreshTrigger,
  } = props;

  // =====================================================================
  // Configuration
  // =====================================================================

  const { showMessage } = useMessages();
  const configValidation = useMemo(() => BaseExplorerConfigFactory.validateConfig(config), [config]);
  const features = useMemo(() => config.features || {}, [config.features]);
  const presentation = useMemo<PresentationConfig>(
    () =>
      config.presentation || {
        title: config.entityName,
        columnConfig: [],
        styling: { theme: 'default', striped: true, bordered: true, hoverable: true },
        layout: { minHeight: 400, responsive: true, showToolbar: true, showFooter: true },
      },
    [config.presentation, config.entityName],
  );

  // =====================================================================
  // Per-user persistence — load BEFORE useState so initializers can read it.
  // Saves sort + filters + widths + window pos/size + pageSize to localStorage.
  // =====================================================================
  const { userInfo } = useFusionAuth();
  const userId = (userInfo as any)?.sub ?? null;
  const persistedRef = useRef<ReturnType<typeof loadExplorerState>>(null);
  if (persistedRef.current === null) {
    persistedRef.current = loadExplorerState(config.entityName, userId);
  }

  // =====================================================================
  // Explorer state
  // =====================================================================

  const [state, setState] = useState<ExplorerState<T>>({
    entities: [],
    loading: false,
    error: null,
    currentPage: 1,
    pageSize: persistedRef.current?.pageSize ?? features.pagination?.defaultPageSize ?? 25,
    totalCount: 0,
    searchTerm: initialSearchTerm,
    crosslinkSearch: initialCrosslinkSearch || null,
    sortConfig: null,
    filters: {},
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const [savedColumnWidths, setSavedColumnWidths] = useState<Record<string, number>>(
    persistedRef.current?.widths ?? {}
  );

  // Deep-link navigation (e.g. clicking "Data" on a weather station opens
  // this explorer with the station preset). When initialFilters is supplied
  // by the caller, treat it as a navigation intent and reset persisted
  // filters AND sort — otherwise a stale persisted filter can hide the
  // very rows the user navigated to see. Window position, size, and
  // column widths still persist (those don't affect which rows render).
  const hasInitialFilters = !!initialFilters && Object.keys(initialFilters).length > 0;

  // Apply persisted sortConfig once after first render. Done via effect (not
  // useState initializer) so the sort change kicks the existing useEffect
  // that fetches data — otherwise initial load would use the wrong sort.
  useEffect(() => {
    if (hasInitialFilters) return;
    const persistedSort = persistedRef.current?.sort;
    if (persistedSort) {
      setState(prev => ({ ...prev, sortConfig: persistedSort }));
      return;
    }
    // No persisted sort → fall back to the config's defaultSort. Without
    // this, every explorer's features.sorting.defaultSort was a no-op (#567
    // surfaced this: WFE config declared sort-by-start-date-desc but the
    // table opened unsorted).
    const defaultSort = features.sorting?.defaultSort?.[0];
    if (defaultSort) {
      setState(prev => ({
        ...prev,
        sortConfig: { field: defaultSort.field, direction: defaultSort.direction },
      }));
    }
    // run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter panel state — deep-link initialFilters wins over persisted set
  // (see hasInitialFilters note above).
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>(
    hasInitialFilters ? (initialFilters as Record<string, any>) : (persistedRef.current?.filters ?? {})
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterPanelColumn, setFilterPanelColumn] = useState<string | null>(null);
  // Narrow-screen search affordance: tap the magnifier icon to expand the
  // input; the input stays visible whenever there's an active search term.
  const [searchInputOpen, setSearchInputOpen] = useState(false);
  // When the viewport drops below the mobile-column threshold, columns
  // declared `mobile: { hidden: true }` disappear. This flag lets the user
  // opt back into the full column set (with horizontal scroll).
  const [showAllColumns, setShowAllColumns] = useState(false);
  // Mirror the persisted/initial filter set in the ref the data-fetch reads,
  // otherwise activeFilters state has the saved filters but the first query
  // still uses {} and the user has to re-apply filters on open.
  const activeFiltersRef = useRef<Record<string, any>>(
    hasInitialFilters ? (initialFilters as Record<string, any>) : (persistedRef.current?.filters ?? {})
  );
  const searchTermRef = useRef<string>(state.searchTerm);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // (Persistence effect + reset handler are placed below the windowState
  // declaration so they can reference window position/size.)

  useEffect(() => {
    onActiveFiltersChange?.(activeFilters);
  }, [activeFilters, onActiveFiltersChange]);

  useEffect(() => {
    searchTermRef.current = state.searchTerm;
  }, [state.searchTerm]);

  // =====================================================================
  // Window state (position/size/isMinimized/zIndex)
  // =====================================================================

  const windowIdRef = useRef<string>(generateWindowId('explorer'));
  const windowRef = useRef<HTMLDivElement>(null);

  // Seed windowState. If persisted state exists for this user+explorer
  // restore it (clampToViewport rescues an off-screen save from a previous
  // session — e.g. moved to a now-disconnected second monitor). Otherwise
  // strictly clamp the wrapper-supplied initial position/size to the
  // viewport so AuditExplorer-style hardcoded 1200×700 defaults don't put
  // the resize handle off-screen on a 1470×956 M2 Air (#504).
  const persistedWindow = clampToViewport(
    persistedRef.current?.position,
    persistedRef.current?.size,
  );
  const initialClamped =
    typeof window === 'undefined'
      ? { position: initialPosition, size: initialSize }
      : clampWindowToViewport(
          initialPosition,
          initialSize,
          { width: window.innerWidth, height: window.innerHeight },
          20,
          { top: MAP_TOP_OFFSET },
        );
  const [windowState, setWindowState] = useState<WindowState>({
    position: persistedWindow.position ?? initialClamped.position,
    size: persistedWindow.size ?? initialClamped.size,
    isMinimized: false,
    zIndex: 1000,
  });

  // Re-clamp on browser resize so an explorer opened on a large screen and
  // then moved to a smaller one stays fully visible.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      setWindowState(prev => {
        const clamped = clampWindowToViewport(
          prev.position,
          prev.size,
          { width: window.innerWidth, height: window.innerHeight },
          20,
          { top: MAP_TOP_OFFSET },
        );
        if (
          clamped.position.x === prev.position.x &&
          clamped.position.y === prev.position.y &&
          clamped.size.width === prev.size.width &&
          clamped.size.height === prev.size.height
        ) {
          return prev;
        }
        return { ...prev, position: clamped.position, size: clamped.size };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const setPosition = useCallback((pos: WindowPosition) => {
    setWindowState(prev => (prev.position.x === pos.x && prev.position.y === pos.y ? prev : { ...prev, position: pos }));
  }, []);
  const setSize = useCallback((s: WindowSize) => {
    setWindowState(prev => (prev.size.width === s.width && prev.size.height === s.height ? prev : { ...prev, size: s }));
  }, []);
  const setIsMinimized = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
    setWindowState(prev => {
      const value = typeof next === 'function' ? next(prev.isMinimized) : next;
      return prev.isMinimized === value ? prev : { ...prev, isMinimized: value };
    });
  }, []);

  // Persist sort + filters + widths + window pos/size whenever any change.
  // localStorage writes are tiny so no debounce needed; the entire object is
  // rewritten on each change.
  useEffect(() => {
    saveExplorerState(config.entityName, userId, {
      sort: state.sortConfig,
      filters: activeFilters,
      widths: savedColumnWidths,
      position: windowState.position,
      size: windowState.size,
      pageSize: state.pageSize,
    });
  }, [
    config.entityName, userId,
    state.sortConfig, activeFilters, savedColumnWidths,
    windowState.position, windowState.size, state.pageSize,
  ]);

  // Reset button handler — clears localStorage entry and reloads defaults.
  // Bumps refreshKey so HOTDataGrid remounts and picks up the cleared widths.
  const handleResetExplorerLayout = useCallback(() => {
    clearExplorerState(config.entityName, userId);
    const defaultSort = features.sorting?.defaultSort?.[0];
    setState(prev => ({
      ...prev,
      // Reset restores the config's defaultSort, not nothing — operators
      // expect "reset" to bring back the as-shipped view (#567).
      sortConfig: defaultSort
        ? { field: defaultSort.field, direction: defaultSort.direction }
        : null,
      pageSize: features.pagination?.defaultPageSize ?? 25,
      currentPage: 1,
    }));
    setActiveFilters({});
    activeFiltersRef.current = {};
    setSavedColumnWidths({});
    setWindowState(prev => ({
      ...prev,
      position: initialPosition,
      size: initialSize,
    }));
    setRefreshKey(k => k + 1);
  }, [config.entityName, userId, initialPosition, initialSize, features.pagination?.defaultPageSize, features.sorting?.defaultSort]);

  // Register with window manager on mount
  useEffect(() => {
    if (!isWindowed || !windowRef.current) return;
    const initialZIndex = windowManager.registerWindow(windowIdRef.current, windowRef.current);
    setWindowState(prev => ({ ...prev, zIndex: initialZIndex }));
    onZIndexUpdate?.(initialZIndex);
    return () => { windowManager.unregisterWindow(windowIdRef.current); };
    // onZIndexUpdate intentionally excluded to avoid re-registration loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWindowed]);

  useEffect(() => {
    if (isWindowed && windowRef.current) {
      windowManager.updateWindowElement(windowIdRef.current, windowRef.current);
    }
  });

  const bringToFront = useCallback(() => {
    if (!isWindowed) return;
    const newZIndex = windowManager.bringToFront(windowIdRef.current);
    setWindowState(prev => (prev.zIndex === newZIndex ? prev : { ...prev, zIndex: newZIndex }));
    onZIndexUpdate?.(newZIndex);
  }, [isWindowed, onZIndexUpdate]);

  // Drag + resize via hooks
  const { headerRef, handleMouseDown: onHeaderMouseDown, handleTouchStart: onHeaderTouchStart } = useWindowDrag({
    enabled: isWindowed && draggable,
    position: windowState.position,
    size: windowState.size,
    setPosition,
    onStart: bringToFront,
  });
  const { handleMouseDown: onResizeMouseDown, handleTouchStart: onResizeTouchStart } = useWindowResize({
    enabled: isWindowed && resizable,
    size: windowState.size,
    position: windowState.position,
    windowRef,
    setSize,
    onStart: bringToFront,
    headerHeight: HEADER_HEIGHT,
  });

  // =====================================================================
  // Window button / click handlers
  // =====================================================================

  const handleWindowClose = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onWindowClose?.();
  }, [onWindowClose]);

  const handleWindowMinimize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMinimized(prev => !prev);
    onWindowMinimize?.();
  }, [onWindowMinimize, setIsMinimized]);

  const handleWindowClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('input') ||
      target.closest('textarea') ||
      target.closest('select') ||
      target.closest('button') ||
      target.closest('.window-resize-handle')
    ) {
      return;
    }
    bringToFront();
  }, [bringToFront]);

  // =====================================================================
  // Imperative refs for parents
  // =====================================================================

  useEffect(() => {
    if (!minimizeRef) return;
    minimizeRef.current = () => setIsMinimized(true);
    return () => { minimizeRef.current = null; };
  }, [minimizeRef, setIsMinimized]);

  useEffect(() => {
    if (!restoreRef) return;
    restoreRef.current = () => setIsMinimized(false);
    return () => { restoreRef.current = null; };
  }, [restoreRef, setIsMinimized]);

  // searchRef wired below once performSearch is defined

  // =====================================================================
  // Data fetching
  // =====================================================================

  // Single source of truth for query construction. Both fetchData (sort/page/
  // crosslink/refresh-driven) and performSearch (search-input-driven) call this
  // so all three filtering dimensions — text search, quick filters, and
  // crosslink search — are always sent together as an AND. Without this,
  // search-input changes would issue queries that drop the active filters and
  // crosslink, producing inconsistent results between paths.
  const buildQueryParams = useCallback((searchTerm: string, page: number) => {
    const pageSize = state.pageSize;
    const currentFilters = activeFiltersRef.current;
    const quickFilters = features.filtering?.quickFilters || [];
    const filterParams: FilterParam[] = [];
    for (const [field, value] of Object.entries(currentFilters)) {
      if (value == null || value === '') continue;
      const qf = quickFilters.find((q: { field: string; type?: string }) => q.field === field);
      if (qf?.type === 'dateRange') {
        const { from, to } = value as { from?: string; to?: string };
        if (from) filterParams.push({ field, operator: 'gte', value: from });
        if (to) filterParams.push({ field, operator: 'lte', value: to });
      } else if (qf?.type === 'textSearch') {
        filterParams.push({ field, operator: 'contains', value });
      } else {
        filterParams.push({ field, operator: 'eq', value });
      }
    }
    return {
      page,
      limit: pageSize,
      sort: state.sortConfig ? [{ field: state.sortConfig.field, direction: state.sortConfig.direction }] : undefined,
      search: searchTerm
        ? { term: searchTerm, fields: features.searching?.searchFields || config.displayFields.map(String) }
        : undefined,
      crosslinkSearch: state.crosslinkSearch
        ? { term: state.crosslinkSearch.term, type: state.crosslinkSearch.type }
        : undefined,
      filters: filterParams.length > 0 ? filterParams : undefined,
    };
  }, [
    state.sortConfig,
    state.crosslinkSearch,
    state.pageSize,
    config.displayFields,
    features.searching?.searchFields,
    features.filtering?.quickFilters,
  ]);

  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);

  // #866 U3 — every data request (structural fetch, auto-refresh tick, search)
  // takes a ticket; only the latest ticket may write results. Without it a slow
  // poll response for page 1 could land after the user's page-2 response and
  // put page-1 rows under a "page 2" footer. A silent tick is skipped while a
  // user-driven request is in flight: that request already brings fresh rows,
  // and a tick built from the previous page/search must not supersede it.
  const requestSeqRef = useRef(0);
  const userRequestPendingRef = useRef(false);

  const fetchData = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (silent && userRequestPendingRef.current) return;
    const tStart = Date.now();
    const seq = ++requestSeqRef.current;
    userRequestPendingRef.current = !silent;
    if (!silent) {
      setState(prev => ({ ...prev, loading: true, error: null }));
    } else {
      setIsAutoRefreshing(true);
    }
    try {
      const queryParams = buildQueryParams(searchTermRef.current, state.currentPage);
      const result = await config.serviceAdapter.fetchData(queryParams);
      if (seq !== requestSeqRef.current) return;
      setState(prev => ({ ...prev, entities: result.data, totalCount: result.total, loading: false }));
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch data',
      }));
    } finally {
      if (seq === requestSeqRef.current) userRequestPendingRef.current = false;
      if (silent) {
        // Keep the spinner visible for at least 1s so a fast fetch is still perceptible.
        const elapsed = Date.now() - tStart;
        const remaining = Math.max(0, 1000 - elapsed);
        window.setTimeout(() => setIsAutoRefreshing(false), remaining);
      }
    }
  }, [
    state.currentPage,
    buildQueryParams,
    config.serviceAdapter,
  ]);

  // Reset page to 1 when external refreshTrigger changes
  const prevRefreshTrigger = useRef(refreshTrigger);
  useEffect(() => {
    if (refreshTrigger !== prevRefreshTrigger.current) {
      prevRefreshTrigger.current = refreshTrigger;
      if (state.currentPage !== 1) {
        setState(prev => ({ ...prev, currentPage: 1 }));
      }
    }
  }, [refreshTrigger, state.currentPage]);

  // Fetch on structural changes (not search input — that's handled in performSearch)
  useEffect(() => {
    fetchData();
    // fetchData intentionally omitted from deps — the real deps are listed explicitly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.pageSize, state.sortConfig, state.crosslinkSearch, refreshKey, refreshTrigger]);

  // Auto-refresh: silent re-fetch on config-defined cadence. Pauses when tab hidden.
  // Silent mode skips the table loading overlay to prevent flash on each tick.
  const autoRefreshMs = config.autoRefreshIntervalMs;
  useEffect(() => {
    if (!autoRefreshMs || autoRefreshMs <= 0) return;
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      fetchData({ silent: true });
    };
    const id = window.setInterval(tick, autoRefreshMs);
    return () => window.clearInterval(id);
  }, [autoRefreshMs, fetchData]);

  // =====================================================================
  // Sort + pagination
  // =====================================================================

  const handleSort = useCallback((field: string) => {
    if (!features.sorting?.enabled) return;
    setState(prev => {
      const current = prev.sortConfig;
      if (!current || current.field !== field) return { ...prev, sortConfig: { field, direction: 'asc' } };
      if (current.direction === 'asc') return { ...prev, sortConfig: { field, direction: 'desc' } };
      return { ...prev, sortConfig: null };
    });
  }, [features.sorting?.enabled]);

  const handlePageChange = useCallback((page: number) => {
    setState(prev => ({ ...prev, currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setState(prev => ({ ...prev, pageSize, currentPage: 1 }));
  }, []);

  // =====================================================================
  // Filter handlers
  // =====================================================================

  const handleFilterClick = useCallback((field: string) => {
    setFilterPanelColumn(prev => {
      if (prev === field) {
        setFilterPanelOpen(open => !open);
        return prev;
      }
      setFilterPanelOpen(true);
      return field;
    });
  }, []);

  const handleFilterChange = useCallback((field: string, value: any) => {
    setActiveFilters(prev => {
      const next = { ...prev, [field]: value };
      activeFiltersRef.current = next;
      return next;
    });
  }, []);

  const handleFilterApply = useCallback(() => {
    setFilterPanelOpen(false);
    setState(prev => ({ ...prev, currentPage: 1 }));
    setRefreshKey(k => k + 1);
  }, []);

  const handleFilterClear = useCallback((field: string) => {
    setActiveFilters(prev => {
      const next = { ...prev };
      delete next[field];
      activeFiltersRef.current = next;
      return next;
    });
    setFilterPanelOpen(false);
    setState(prev => ({ ...prev, currentPage: 1 }));
    setRefreshKey(k => k + 1);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setActiveFilters({});
    activeFiltersRef.current = {};
    setFilterPanelOpen(false);
    setState(prev => ({ ...prev, currentPage: 1 }));
    setRefreshKey(k => k + 1);
  }, []);

  // =====================================================================
  // Export — count-then-confirm via the shared useExportConfirm hook.
  // =====================================================================

  // Snapshot the export URL params at the moment the user clicks Export so
  // confirmExport runs against the same filter set the count was based on.
  const exportParamsRef = useRef<URLSearchParams | null>(null);

  const { handleExport, dialogProps: exportDialogProps } = useExportConfirm({
    fetchCount: useCallback(async () => {
      if (!features.export?.enabled || !features.export?.entity) {
        return { count: 0, large: false };
      }
      const entity = features.export.entity;
      const params = config.serviceAdapter.buildExportParams?.(
        buildQueryParams(searchTermRef.current, 0),
      ) ?? new URLSearchParams();
      exportParamsRef.current = params;
      const res = await fetch(`/api/export/${entity}/count?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.message ?? 'Network error while counting rows.');
      const data = await res.json();
      return { count: data.count, large: !!data.large };
    }, [features.export, config.serviceAdapter, buildQueryParams]),

    performExport: useCallback(() => {
      const entity = features.export?.entity;
      const params = exportParamsRef.current;
      if (!entity || !params) return;
      void downloadExportCsv({
        url: `/api/export/${entity}?${params.toString()}`,
        filename: `${entity}_export`,
        onMessage: showMessage,
      });
    }, [features.export, showMessage]),
  });

  // Spatial formats (#875): offered when the explorer names a spatial endpoint.
  // CSV keeps the count-then-confirm path above; a spatial file is built from the
  // same filters and downloaded directly.
  const exportFormats = (features.export?.formats ?? ['csv']).filter(
    (f): f is ExplorerExportFormat => f === 'csv' || f === 'shapefile' || f === 'geojson' || f === 'kml',
  );
  const offersSpatial = !!features.export?.spatialEndpoint && exportFormats.some((f) => f !== 'csv');
  const [exportFormatOpen, setExportFormatOpen] = useState(false);

  const handleExportFormat = useCallback((format: ExplorerExportFormat) => {
    setExportFormatOpen(false);
    if (format === 'csv') {
      void handleExport();
      return;
    }
    const params = config.serviceAdapter.buildExportParams?.(
      buildQueryParams(searchTermRef.current, 0),
    ) ?? new URLSearchParams();
    params.set('format', format);
    params.set('crs', '4326');
    void downloadExportCsv({
      url: `${features.export?.spatialEndpoint}?${params.toString()}`,
      filename: features.export?.entity ?? 'export',
      onMessage: showMessage,
      useServerFilename: true,
    });
  }, [handleExport, config.serviceAdapter, buildQueryParams, features.export, showMessage]);

  // =====================================================================
  // Search
  // =====================================================================

  const performSearch = useCallback(async (term: string) => {
    if (!isAuthenticated) return;

    if (searchInputRef.current) searchInputRef.current.value = term;
    const seq = ++requestSeqRef.current;
    userRequestPendingRef.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Page is 1-based (see paginateClientSide/pageToOffset). A new search
      // resets to the first page — must be 1, not 0. Passing 0 makes
      // paginateClientSide compute a negative offset (slice(-limit, 0)) and
      // return an empty page, so the search appeared to find nothing (#586).
      // This matches the currentPage: 1 set in the state update below.
      const queryParams = buildQueryParams(term, 1);
      const result = await config.serviceAdapter.fetchData(queryParams);
      if (seq !== requestSeqRef.current) return;
      setState(prev => ({
        ...prev,
        entities: result.data,
        totalCount: result.total,
        currentPage: 1,
        searchTerm: term,
        loading: false,
      }));
      if (searchInputRef.current && searchInputRef.current.value !== term) {
        searchInputRef.current.value = term;
      }
      onSearchChange?.(term);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch data',
      }));
      if (searchInputRef.current && searchInputRef.current.value !== term) {
        searchInputRef.current.value = term;
      }
    } finally {
      if (seq === requestSeqRef.current) userRequestPendingRef.current = false;
    }
  }, [
    isAuthenticated,
    buildQueryParams,
    config.serviceAdapter,
    onSearchChange,
  ]);

  useEffect(() => {
    if (!searchRef) return;
    searchRef.current = (term: string) => performSearch(term);
    return () => { searchRef.current = null; };
  }, [searchRef, performSearch]);

  const handleSearchInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => performSearch(value.trim()), 1500);
  }, [performSearch]);

  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  }, []);

  // Keep uncontrolled input in sync with programmatic searchTerm changes
  useLayoutEffect(() => {
    if (searchInputRef.current && searchInputRef.current.value !== state.searchTerm) {
      searchInputRef.current.value = state.searchTerm;
    }
  }, [state.searchTerm]);

  // =====================================================================
  // Crosslink search
  // =====================================================================

  const executeCrosslinkSearch = useCallback((search: { term: string; type: string }) => {
    // Crosslink and normal search compose as AND now — neither path clears the
    // other. The grid shows entries matching both the crosslink target and
    // any active filter / search term.
    setState(prev => ({
      ...prev,
      crosslinkSearch: search,
      currentPage: 1,
    }));
    onCrosslinkSearchChange?.(search);
  }, [onCrosslinkSearchChange]);

  useEffect(() => {
    if (
      initialCrosslinkSearch &&
      (!state.crosslinkSearch ||
        state.crosslinkSearch.term !== initialCrosslinkSearch.term ||
        state.crosslinkSearch.type !== initialCrosslinkSearch.type)
    ) {
      executeCrosslinkSearch(initialCrosslinkSearch);
    } else if (!initialCrosslinkSearch && state.crosslinkSearch) {
      // Parent cleared crosslink (e.g. via the indicator's X button) —
      // sync CBE's internal state so the next fetch drops the crosslink
      // term while preserving search and filters.
      setState(prev => ({ ...prev, crosslinkSearch: null }));
    }
  }, [initialCrosslinkSearch, executeCrosslinkSearch, state.crosslinkSearch]);

  // =====================================================================
  // Field transformation + columns
  // =====================================================================

  const transformFieldValue = useCallback((entity: T, field: keyof T): string => {
    const value = entity[field];
    const transformer = config.fieldTransformers?.[field];
    if (transformer) return transformer.transform(value, entity);
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }, [config.fieldTransformers]);

  // Threshold at 400px so columns only collapse on genuinely small phones;
  // an iPhone 14 Pro (430px) and larger keeps the full column set and uses
  // horizontal scroll. Reactive via a resize listener so users resizing a
  // desktop window past the threshold see the change immediately.
  const MOBILE_COLUMN_BREAKPOINT = 400;
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_COLUMN_BREAKPOINT,
  );
  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth <= MOBILE_COLUMN_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const columns = useMemo<ColumnConfig[]>(() => {
    const baseColumns: ColumnConfig[] = presentation.columnConfig.length > 0
      ? presentation.columnConfig
      : config.displayFields.map(field => ({
        field: String(field),
        title: String(field).replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
        resizable: true,
        sortable: features.sorting?.enabled || false,
        filterable: features.filtering?.enabled || false,
        hidden: false,
      }));

    const merged = baseColumns.map((col, index) => {
      const overrides = isMobileView ? col.mobile : col.desktop;
      // On mobile, "show all columns" opt-in ignores the mobile.hidden flag.
      const overrideHidden = isMobileView && showAllColumns ? undefined : overrides?.hidden;
      return {
        ...col,
        width: overrides?.width ?? col.width,
        minWidth: overrides?.minWidth ?? col.minWidth,
        maxWidth: overrides?.maxWidth ?? col.maxWidth,
        hidden: props.hiddenColumns?.includes(col.field)
          ? true
          : props.shownColumns?.includes(col.field)
            ? false
            : (overrideHidden ?? col.hidden),
        _order: overrides?.order ?? col.order ?? index,
      } as ColumnConfig & { _order: number };
    });

    return merged.sort((a: any, b: any) => a._order - b._order);
  }, [
    presentation.columnConfig,
    config.displayFields,
    features.sorting?.enabled,
    features.filtering?.enabled,
    isMobileView,
    showAllColumns,
    props.hiddenColumns,
    props.shownColumns,
  ]);

  // Does any column have `mobile: { hidden: true }`? If not, the toggle is
  // pointless so we don't render it.
  const hasMobileHiddenColumns = useMemo(
    () => (presentation.columnConfig || []).some(c => c.mobile?.hidden),
    [presentation.columnConfig],
  );

  const activeFilterCount = Object.keys(activeFilters).filter(k => activeFilters[k] != null && activeFilters[k] !== '').length;
  const currentFilterConfig = features.filtering?.quickFilters?.find(
    (qf: { field: string }) => qf.field === filterPanelColumn,
  ) || null;

  // =====================================================================
  // Render
  // =====================================================================

  // Memoize the inner explorer so window-state-only re-renders (drag, resize,
  // z-index, minimize) don't re-render the table subtree.
  const explorerBody = useMemo(() => (
    <div
      className={`base-explorer ${presentation.styling?.theme || 'default'} ${className}`}
      data-testid={testId}
      onClick={(e) => {
        if (filterPanelOpen && !(e.target as HTMLElement).closest('.filter-panel') && !(e.target as HTMLElement).closest('.column-filter-icon')) {
          setFilterPanelOpen(false);
        }
      }}
      style={{
        minHeight: presentation.layout?.minHeight,
        height: isWindowed ? '100%' : 'auto',
        overflow: isWindowed ? 'hidden' : 'visible',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',  // anchor for absolute children (e.g. ExportConfirmDialog)
      }}
    >
      <div className="explorer-header">
        <div className="explorer-header-top">
          <div className="title-section">
            <h2>
              {presentation.icon && (
                typeof presentation.icon === 'string'
                  ? <span style={{ marginRight: 8 }}>{presentation.icon}</span>
                  : <FontAwesomeIcon icon={presentation.icon} style={{ marginRight: 8 }} />
              )}
              {presentation.title}
            </h2>
            {presentation.subtitle && <p className="subtitle">{presentation.subtitle}</p>}
            <div className="auto-refresh-indicator" aria-live="polite">
              <span className="auto-refresh-label">
                {autoRefreshMs && autoRefreshMs > 0
                  ? `refreshes every ${autoRefreshMs >= 60000
                      ? `${Math.round(autoRefreshMs / 60000)}m`
                      : `${Math.round(autoRefreshMs / 1000)}s`}`
                  : 'no auto refresh'}
              </span>
              <button
                type="button"
                className="auto-refresh-button"
                onClick={() => fetchData({ silent: true })}
                disabled={isAutoRefreshing}
                title="Refresh now"
                aria-label="Refresh now"
              >
                <FontAwesomeIcon
                  icon={isAutoRefreshing ? faSpinner : faRotateRight}
                  spin={isAutoRefreshing}
                />
              </button>
            </div>
          </div>

          {presentation.layout?.showToolbar && (
            <div className="header-actions">
              {features.filtering?.enabled && activeFilterCount > 0 && (
                <FilterBadge activeFilterCount={activeFilterCount} onClearAll={handleClearAllFilters} />
              )}
              {/* Search toggle icon — shown on narrow containers (@container
                  rule hides it >600px). Rendered regardless of whether
                  features.searching.enabled, so explorers that inject
                  their own search via portal (e.g. DLE) also get the toggle. */}
              {(
                <button
                  type="button"
                  className="search-icon-btn btn-primary"
                  title="Search"
                  aria-label="Search"
                  onMouseDown={(e) => { e.preventDefault(); }}
                  onClick={() => {
                    const isOpen = searchInputOpen || !!state.searchTerm;
                    if (isOpen) {
                      // Second tap — close the row immediately and clear any
                      // active search. We clear state.searchTerm synchronously
                      // (instead of awaiting performSearch) so showInput flips
                      // to false right away and the row collapses now; the
                      // data refetch runs via refreshKey.
                      if (searchInputRef.current) searchInputRef.current.value = '';
                      // Cancel any pending debounced search — otherwise a
                      // queued timer from typing will fire after we close and
                      // re-set state.searchTerm, reopening the row.
                      if (debounceTimerRef.current) {
                        clearTimeout(debounceTimerRef.current);
                        debounceTimerRef.current = null;
                      }
                      if (state.searchTerm) {
                        searchTermRef.current = '';
                        setState(prev => ({ ...prev, searchTerm: '', currentPage: 1 }));
                        setRefreshKey(k => k + 1);
                        onSearchChange?.('');
                      }
                      setSearchInputOpen(false);
                    } else {
                      setSearchInputOpen(true);
                      setTimeout(() => searchInputRef.current?.focus(), 0);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </button>
              )}
              {features.export?.enabled && features.export?.entity && (
                <button
                  onClick={offersSpatial ? () => setExportFormatOpen(true) : handleExport}
                  className="btn-export-csv btn-primary"
                  title={offersSpatial ? 'Export' : 'Export CSV'}
                  aria-label={offersSpatial ? 'Export' : 'Export CSV'}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span className="btn-label">{offersSpatial ? 'Export…' : 'Export CSV'}</span>
                </button>
              )}
              {isMobileView && hasMobileHiddenColumns && (
                <button
                  type="button"
                  className={`columns-toggle-btn${showAllColumns ? ' columns-toggle-btn--active' : ''}`}
                  onClick={() => setShowAllColumns(v => !v)}
                  title={showAllColumns ? 'Collapse to mobile view' : 'Show all columns'}
                  aria-label={showAllColumns ? 'Collapse to mobile view' : 'Show all columns'}
                  aria-pressed={showAllColumns}
                >
                  <FontAwesomeIcon icon={faTableColumns} />
                </button>
              )}
              {customToolbar && (
                <div className="custom-toolbar-container">{customToolbar}</div>
              )}
              {/* Help always renders last → far right of header actions */}
              {help && (
                <div className="explorer-help-slot">{help}</div>
              )}
            </div>
          )}
        </div>

        {presentation.layout?.showToolbar && (() => {
          const hasActiveSearch = !!state.searchTerm;
          const showInput = searchInputOpen || hasActiveSearch;
          const searchEnabled = !!features.searching?.enabled;
          return (
            <div className={`explorer-toolbar main-explorer-toolbar${showInput ? ' explorer-toolbar--search-open' : ''}`}>
              {searchEnabled && (
              <div className="explorer-search-wrapper">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="search-input"
                  id="explorer-search-input"
                  name="explorerSearch"
                  placeholder={features.searching?.placeholder || 'Search...'}
                  onChange={handleSearchInput}
                  autoComplete="off"
                />
                <button
                  onClick={() => {
                    if (searchInputRef.current) searchInputRef.current.value = '';
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                      debounceTimerRef.current = null;
                    }
                    performSearch('');
                    setSearchInputOpen(false);
                  }}
                  className="clear-search-btn"
                  title="Clear search"
                  aria-label="Clear search"
                >
                  &times;
                </button>
              </div>
              )}
            </div>
          );
        })()}
      </div>

      {children}

      {features.filtering?.enabled && filterPanelOpen && currentFilterConfig && (
        <FilterPanel
          isOpen={filterPanelOpen}
          column={currentFilterConfig}
          value={activeFilters[currentFilterConfig.field]}
          onFilterChange={handleFilterChange}
          onApply={handleFilterApply}
          onClear={handleFilterClear}
          onClose={() => setFilterPanelOpen(false)}
        />
      )}

      <ExportFormatModal
        isOpen={exportFormatOpen}
        formats={exportFormats}
        onClose={() => setExportFormatOpen(false)}
        onExport={handleExportFormat}
      />
      <ExportConfirmDialog
        {...exportDialogProps}
        entityLabel={presentation.title?.toLowerCase()}
        title={`${presentation.title ?? windowTitle ?? 'Export'} Export`}
      />

      <ResultsSection<T>
        entities={state.entities}
        loading={state.loading}
        error={state.error}
        totalCount={state.totalCount}
        currentPage={state.currentPage}
        pageSize={state.pageSize}
        sortConfig={state.sortConfig}
        columns={columns}
        features={features}
        config={config}
        onEntitySelect={onEntitySelect}
        onEntityView={onEntityView}
        onEntityEdit={onEntityEdit}
        onEntityDelete={onEntityDelete}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        transformFieldValue={transformFieldValue}
        onRetry={fetchData}
        isWindowed={isWindowed}
        onFilterClick={handleFilterClick}
        activeFilters={activeFilters}
        savedColumnWidths={savedColumnWidths}
        onColumnResize={(field, width) =>
          setSavedColumnWidths(prev => ({ ...prev, [field]: width }))
        }
      />

      {presentation.layout?.showFooter && (
        <div className="explorer-footer">
          <div className="footer-info">
            {process.env.NODE_ENV === 'development' && (
              <>
                <span> 👀 Entity Type: {config.entityName}</span>
                <span>Complexity: {config.complexityLevel}</span>
                <span>Fields: {config.displayFields.length}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  ), [
    // Everything that affects the explorer body render
    className,
    testId,
    isWindowed,
    customToolbar,
    children,
    presentation,
    features,
    activeFilterCount,
    activeFilters,
    filterPanelOpen,
    currentFilterConfig,
    columns,
    config,
    state.entities,
    state.loading,
    state.error,
    state.totalCount,
    state.currentPage,
    state.pageSize,
    state.sortConfig,
    state.searchTerm,
    searchInputOpen,
    isMobileView,
    showAllColumns,
    hasMobileHiddenColumns,
    onEntitySelect,
    onEntityView,
    onEntityEdit,
    onEntityDelete,
    handleSort,
    handlePageChange,
    handlePageSizeChange,
    transformFieldValue,
    fetchData,
    handleFilterClick,
    handleSearchInput,
    performSearch,
    handleExport,
    handleClearAllFilters,
    handleFilterChange,
    handleFilterApply,
    handleFilterClear,
    exportDialogProps,
  ]);

  if (!configValidation.isValid) {
    const errorBody = (
      <div className={`base-explorer error ${className}`} data-testid={testId}>
        <div className="config-errors">
          <h3>Configuration Errors</h3>
          {configValidation.errors.map((e, i) => (
            <div key={i} className="error-message"><strong>{e.field}:</strong> {e.message}</div>
          ))}
        </div>
      </div>
    );
    if (!isWindowed) return errorBody;
    return (
      <WindowChrome
        windowRef={windowRef}
        headerRef={headerRef}
        windowState={windowState}
        draggable={draggable}
        resizable={resizable}
        showMinimizeButton={showMinimizeButton}
        showCloseButton={showCloseButton}
        windowTitle={windowTitle}
        onWindowClick={handleWindowClick}
        onHeaderMouseDown={onHeaderMouseDown}
        onHeaderTouchStart={onHeaderTouchStart}
        onResizeMouseDown={onResizeMouseDown}
        onResizeTouchStart={onResizeTouchStart}
        onMinimizeClick={handleWindowMinimize}
        onCloseClick={handleWindowClose}
      >
        {errorBody}
      </WindowChrome>
    );
  }

  if (!isWindowed) return explorerBody;

  return (
    <WindowChrome
      windowRef={windowRef}
      headerRef={headerRef}
      windowState={windowState}
      draggable={draggable}
      resizable={resizable}
      showMinimizeButton={showMinimizeButton}
      showCloseButton={showCloseButton}
      windowTitle={windowTitle}
      onWindowClick={handleWindowClick}
      onHeaderMouseDown={onHeaderMouseDown}
      onHeaderTouchStart={onHeaderTouchStart}
      onResizeMouseDown={onResizeMouseDown}
      onResizeTouchStart={onResizeTouchStart}
      onMinimizeClick={handleWindowMinimize}
      onCloseClick={handleWindowClose}
      onResetClick={handleResetExplorerLayout}
    >
      {explorerBody}
    </WindowChrome>
  );
}
